import { Module } from "vuex";
import * as firebase from "firebase/app";
import "firebase/firestore";
import router from "@/router";
import { StaffAccessLevels } from "@/enums/StaffAccessLevels";
import { CurrenciesSymbols } from "@/consts/currencies";
import { getWhereIdInIds } from "@/utils/getWhereIdInIds";
import { DefaultEmailTemplateType } from "@/enums/DefaultEmailTemplateType";
import { initFacility } from "@/consts/defaultFacilityValues";
import { serializer } from "@/api/shared/serializer";
import { ServiceStatus } from "@/enums/ServiceStatus";
import { EventsTypes } from "@/enums/EventsTypes";
import { FamilyHubAppStatus } from "@/enums/FamilyHubAppStatus";
import { ResidentStatus } from "@/enums/ResidentStatus";
import { getRegionName } from "@/utils/functionsRegion";
import { getLocation } from "@/utils/weather";

const facilityModule: Module<any, any> = {
  namespaced: false,
  state: {
    facilities: [],
    selectedFacility: null,
    selectedFacilityUsers: [],
    currentFacilityUsers: [],
    facilityEmailTemplates: [],
    facilityEmailTemplateTypes: [],
    facilityMailingLists: [],
    facilityMailingCurrent: null,
    facilityMailingSendedCurrent: null,
    currentEmailTemplate: [],
    currentEmailTemplates: [],
    broadcastTemplates: [],
    onCurrentFacilityUnsubscribe: null,
    isFacilityHasCareAPI: false,
    facilityCareAPIParameters: {},
    surveyTemplates: [],
    currentWeatherData: null
  },

  getters: {
    facilityCurrency: state =>
      CurrenciesSymbols[state.selectedFacility.currencyIso] || "$"
  },

  mutations: {
    setFacilities(state: any, payload: []) {
      state.facilities = payload;
    },
    setSelectedFacility(state: any, payload: []) {
      state.selectedFacility = payload;
    },
    setSelectedFacilityUsers(state: any, payload: []) {
      state.selectedFacilityUsers = payload;
    },
    setCurrentFacilityUsers(state: any, payload: []) {
      state.currentFacilityUsers = payload;
    },
    setOrganisations(state: any, payload: []) {
      state.organisations = payload;
    },
    setFacilityEmailTemplates(state: any, payload: []) {
      state.facilityEmailTemplates = payload;
    },
    setFacilityEmailTemplateTypes(state: any, payload: []) {
      state.facilityEmailTemplateTypes = payload;
    },
    setFacilityMailingLists(state: any, payload: []) {
      state.facilityMailingLists = payload;
    },
    setFacilityMailingCurrent(state: any, payload: []) {
      state.facilityMailingCurrent = payload;
    },
    setFacilityMailingSendedCurrent(state: any, payload: []) {
      state.facilityMailingSendedCurrent = payload;
    },
    setCurrentEmailTemplate(state: any, payload: []) {
      state.currentEmailTemplate = payload;
    },
    setCurrentEmailTemplates(state: any, payload: []) {
      state.currentEmailTemplates = payload;
    },
    setBroadcastTemplates(state: any, payload: []) {
      state.broadcastTemplates = payload;
    },
    setCareAPIKey(state: any, payload: boolean) {
      state.isFacilityHasCareAPI = payload;
    },
    setCareAPIParameters(state: any, payload: {}) {
      state.facilityCareAPIParameters = payload;
    },
    setEmailSurveyTemplates(state: any, payload: []) {
      state.surveyTemplates = payload;
    },
    setCurrentWeatherData(state: any, payload: {}) {
      state.currentWeatherData = payload;
    },
  },

  actions: {
    async getFacilityReportData({ commit }, { startDate, endDate }) {

      const facility = this.state.authModule.currentFacilityData;
      const isOptInLifestylePrograms = this.state.facilityModule.selectedFacility.isOptInLifestylePrograms;
      const additionalServices = serializer.querySnapshotToDocuments(await firebase.firestore().collection("additionalServices").where("facilityId", "==", facility.id).get())
      const residents = this.state.residentModule.residentsShortData.filter(x => x.status === ResidentStatus.ActiveResident);
      const residentIds = residents.map((doc: any) => doc.id);

      const eventServicesReportPromise = new Promise((resolve) => {
        return firebase.firestore().collection("serviceRequests")
          .where("facilityId", "==", facility.id).get().then((serviceRequestsSnap) => {

            return firebase.firestore().collection("calender")
              .where("facilityId", "==", facility.id)
              .where("type", "in", [EventsTypes.Concierge,
              EventsTypes.ConciergeRequest,
              EventsTypes.PremiumServices,
              EventsTypes.LifestylePrograms,
              EventsTypes.Meal
              ]).get().then((eventsSnap) => {
                const calendarEvents = serializer.querySnapshotToDocuments(eventsSnap)

                const eventsWithAttendees = calendarEvents.map((event: any) => {

                  event.residentsMembersIds = [];
                  event.amount = 0;

                  if (event.attendeesIds?.length) {
                    event.residentsMembersIds = event.attendeesIds
                    event.amount = event.residentsMembersIds.length
                  }

                  else if (event.type === EventsTypes.LifestylePrograms && event.invitees?.length) {
                    const lifestyleProgramId = event.invitees[0];
                    const allInvited = lifestyleProgramId.toLowerCase() === 'all'
                    if (allInvited || !isOptInLifestylePrograms) {
                      event.residentsMembersIds = residentIds
                      event.amount = event.residentsMembersIds.length
                    }
                  }

                  else if (event.type === EventsTypes.PremiumServices && event.invitees?.length) {
                    event.residentsMembersIds = additionalServices.filter((svc: any) => event.invitees.includes(svc.id)).map((x: any) => x.membersIds).reduce((acc, curr) => acc.concat(curr), []);
                    event.amount = event.residentsMembersIds.length
                  }

                  return event;
                });

                const servicesRequestsApproveCompleted: any = [];
                serviceRequestsSnap.docs.forEach(x => {
                  const request = x.data();
                  const exist = additionalServices.some(x => x.id === request.serviceId)
                  if (exist && (request.status === ServiceStatus.Completed || request.status === ServiceStatus.Approved)) {
                    request.id = x.id;
                    servicesRequestsApproveCompleted.push(request);
                  }
                });

                const additionalServicespopulatity = servicesRequestsApproveCompleted.reduce((total: any, current: any) => {
                  const object = total.find(x => x.name === current.serviceName);

                  if (object) {
                    object.amount++
                  } else {
                    total.push({
                      providerName: current.providerName,
                      name: current.serviceName,
                      amount: 1
                    })
                  }
                  return total;
                }, [])

                const servicesRequestsApproveCompletedUnique = [...new Map(servicesRequestsApproveCompleted.map(item => [item.serviceId, item])).values()];
                const servicesBookedSum = servicesRequestsApproveCompleted.reduce((total: number, current: any) => (total || 0) + Number(current.paymentCost || 0), 0);
                const servicesBookedItems = servicesRequestsApproveCompleted.reduce((total: any, current: any) => {
                  if (!total.includes(current.serviceName)) {
                    return [...total, current.serviceName];
                  }
                  return total;
                }, []);

                const servicesBooked = {
                  items: servicesBookedItems,
                  sum: servicesBookedSum
                }

                resolve({
                  servicesRequestsApproveCompleted: servicesRequestsApproveCompletedUnique,
                  servicesBooked: servicesBooked,
                  mostPopularServices: [...additionalServicespopulatity].sort((a: any, b: any) => b.amount - a.amount),
                  leastPopularServices: [...additionalServicespopulatity].sort((a: any, b: any) => a.amount - b.amount),
                  eventsWithAttendees: eventsWithAttendees.filter(x => x.residentsMembersIds.length > 0),
                  mostPopularEvents: [...eventsWithAttendees].sort((a: any, b: any) => b.amount - a.amount),
                  leastPopularEvents: [...eventsWithAttendees].sort((a: any, b: any) => a.amount - b.amount),
                })

              })
          })
      })

      const activitiesReportPromise = new Promise((resolve) => {
        const activitiesPromise = firebase.firestore().collection("adHocActivities").where("facilityId", "==", facility.id).get()
        const activitiesTypesPromise = firebase.firestore().collection("adHocActivitiesTypes").where("facilityId", "==", facility.id).get()
        return Promise.all([activitiesPromise, activitiesTypesPromise]).then((sesponse) => {
          const activities = serializer.querySnapshotToDocuments(sesponse[0])
          const activitiesTypes = serializer.querySnapshotToDocuments(sesponse[1])
          const activitiesWithResidents = activities.filter((x: any) => x.residentId)
          activitiesTypes.forEach((type: any) => { type.amount = activitiesWithResidents.filter((x: any) => x.typeId == type.id).length });
          resolve({
            mostPopularActivities: [...activitiesTypes].sort((a: any, b: any) => b.amount - a.amount),
            leastPopularActivities: [...activitiesTypes].sort((a: any, b: any) => a.amount - b.amount),
            numberOfActivities: activities.length,
          })
        })
      })

      const familyMembersReportPromise = new Promise((resolve) => {
        return firebase.firestore().collection("familyHubUsers").where("facilityId", "==", facility.id).get().then((response) => {
          const familyHubUsers: any = serializer.querySnapshotToDocuments(response)
          const numberOfFamilyHubUsersUsingApp = familyHubUsers.filter((x: any) => x.appStatus === FamilyHubAppStatus.Activated).length
          resolve({
            numberOfFamilyHubUsers: familyHubUsers.length,
            numberOfFamilyHubUsersUsingFamilyHub: numberOfFamilyHubUsersUsingApp
          })
        })
      })

      const staffsReportPromise = new Promise((resolve) => {
        return firebase.firestore().collection("users")
          .where("orgId", "==", facility.orgId)
          .where("accessLevel", "in", Object.values(StaffAccessLevels)).get().then((staffsSnapshot) => {
            resolve({
              numberOfStaffs: staffsSnapshot.size,
            })
          })
      })


      const report = {
        eventServicesReportAsync: eventServicesReportPromise,
        activitiesReportAsync: activitiesReportPromise,
        familyMembersReportAsync: familyMembersReportPromise,
        staffsReportAsync: staffsReportPromise,
        residentsReport: {
          numberOfCreatedResidents: residents.length,
          numberOfResidentsUsingMyHub: residents.filter(x => x.isUsingMyHub === true).length
        }
      }

      return report
    },

    async getFacilities({ commit }) {
      const facility = this.state.authModule.currentFacilityData;
      const facilities = firebase.firestore().collection("facility").where("orgId", "==", facility.orgId);
      
      return await facilities.get().then(querySnapshot => {
        const facilitiesList: Array<any> = [];
        querySnapshot.docs.forEach(x => {
          const thisRes = x.data();
          thisRes.id = x.id;
          facilitiesList.push(thisRes);
        });
        commit("setFacilities", facilitiesList);
      });
    },

    async getBroadcastTemplates({ commit }) {
      const facilities = firebase
        .firestore()
        .collection("emailTemplates")
        .where("facilityId", "==", this.state.authModule.currentFacility)
        .where("type", "==", "Broadcast Email");
      await facilities.get().then(querySnapshot => {
        const facilitiesList: Array<any> = [];

        querySnapshot.docs.forEach(x => {
          const thisRes = x.data();
          thisRes.id = x.id;
          facilitiesList.push(thisRes);
        });
        commit("setBroadcastTemplates", facilitiesList);
      });
    },

    async getFacilityEmailTemplates({ commit }, facilityId = null) {
      const emailTemplates = firebase
        .firestore()
        .collection("emailTemplates")
        .where("facilityId", "==", facilityId ?? this.state.authModule.currentFacility)

      const emailTemplateTypes = (await firebase
        .firestore()
        .collection("emailTemplateTypes")
        .where("facilityId", "==", facilityId ?? this.state.authModule.currentFacility).get()).docs.map(x => ({
          id: x.id,
          ...x.data()
        }));

      await emailTemplates.get().then(querySnapshot => {
        const facilitiesList: Array<any> = [];

        querySnapshot.docs.forEach(x => {
          const thisRes = x.data();
          thisRes.id = x.id;
          if (thisRes.typeId) {
            const type: any = emailTemplateTypes.find(x => x.id === thisRes.typeId)
            thisRes.displayType = type ? type.name : ''
          }
          else {
            thisRes.displayType = thisRes.type
          }
          facilitiesList.push(thisRes);
        });
        commit("setFacilityEmailTemplates", facilitiesList);
      });
    },

    async getFacilityEmailSurveyTemplates({ commit }) {
      const templates = firebase
        .firestore()
        .collection("emailTemplates")
        .where("facilityId", "==", this.state.authModule.currentFacility)
        .where("type", "==", DefaultEmailTemplateType.SurveyEmail);

      await templates.get().then(querySnapshot => {
        const templatesList: Array<any> = [];

        querySnapshot.docs.forEach(x => {
          const thisRes = x.data();
          thisRes.id = x.id;
          templatesList.push(thisRes);
        });
        commit("setEmailSurveyTemplates", templatesList);
      });
    },

    async getFacilityEmailTemplateTypes({ commit }) {
      const facilities = firebase
        .firestore()
        .collection("emailTemplateTypes")
        .where("facilityId", "==", this.state.authModule.currentFacility)
        .orderBy("createdDateTime", "desc")

      await facilities.get().then(querySnapshot => {
        const list: Array<any> = [];

        querySnapshot.docs.forEach(x => {
          const thisRes = x.data();
          thisRes.id = x.id;
          list.push(thisRes);
        });
        commit("setFacilityEmailTemplateTypes", list);
      });
    },

    async getFacilityMailingLists({ commit }) {
      const facilities = firebase
        .firestore()
        .collection("mailingLists")
        .where("facilityId", "==", this.state.authModule.currentFacility);

      return await facilities.get().then(querySnapshot => {
        const list: Array<any> = [];

        querySnapshot.docs.forEach(x => {
          const thisRes = x.data();
          thisRes.id = x.id;
          list.push(thisRes);
        });

        commit("setFacilityMailingLists", list);
        return list;
      });
    },

    async getFacilityMailingById({ commit }, id) {
      return firebase
        .firestore()
        .collection("mailingLists")
        .doc(id)
        .get()
        .then((data) => {
          const doc = data.data();
          doc.id = data.id;
          commit("setFacilityMailingCurrent", doc);
        });
    },

    async getFacilityMailingSendedById({ commit }, data) {
      return firebase
        .firestore()
        .collection("mailingLists")
        .doc(data.id)
        .collection("sended")
        .where("facilityId", "==", data.facilityId)
        .get()
        .then(querySnapshot => {
          const result = querySnapshot.docs.map(data => {
            const doc = data.data();
            return {
              ...doc
            };
          });
          commit("setFacilityMailingSendedCurrent", result);
        });
    },

    async addFacilityMailingLists({ dispatch }, mailing) {
      mailing.facilityId == this.state.authModule.currentFacility
      return firebase
        .firestore()
        .collection("mailingLists")
        .add(mailing)
        .then((data) => {
          dispatch("getFacilityMailingLists")
          return data;
        });
    },

    async deleteFacilityMailingList({ dispatch }, mailing) {

      if (mailing.facilityId != this.state.authModule.currentFacility) {
        throw Error('Cant delete mailing list')
      }

      return firebase
        .firestore()
        .collection("mailingLists")
        .doc(mailing.id)
        .delete()
        .then((data) => {
          dispatch("getFacilityMailingLists")
          return data;
        });
    },

    async updateFacilityMailingLists({ dispatch }, mailing) {
      return firebase
        .firestore()
        .collection("mailingLists")
        .doc(mailing.id)
        .update({
          to: mailing.to,
          method: mailing.method
        })
        .then(() => {
          dispatch("getFacilityMailingLists")
        });
    },

    async getEmailTemplate({ commit }, id) {
      const template = firebase
        .firestore()
        .collection("emailTemplates")
        .doc(id);
      await template.get().then(querySnapshot => {
        const currentTemplate = querySnapshot.data();
        if (currentTemplate != null) {
          currentTemplate.id = querySnapshot.id;
          commit("setCurrentEmailTemplate", currentTemplate);
        }
      });
    },

    async getEmailTemplates({ commit }) {
      const querySnapshot = await firebase
        .firestore()
        .collection("emailTemplates")
        .where('facilityId', '==', this.state.authModule.currentFacility).get()

      const templates = querySnapshot.docs.map(x => ({ id: x.id, ...x.data() }))
      commit("setCurrentEmailTemplates", templates);

      return templates
    },

    async changeEmailTemplateOrderIndex({ dispatch, commit }, data) {
      return await firebase
        .firestore()
        .runTransaction(async (transaction) => {
          const movedTemplateDocRef = firebase.firestore().collection('emailTemplates').doc(data.movedTemplateId);
          transaction.update(movedTemplateDocRef, {
            orderIndex: data.newIndex
          })
          const replacedTemplateDocRef = firebase.firestore().collection('emailTemplates').doc(data.replacedTemplateId);
          transaction.update(replacedTemplateDocRef, {
            orderIndex: data.oldIndex
          })
        })
    },

    async updateEmailTemplate({ dispatch, commit }, emailTemplate) {
      const htmlTemplate = emailTemplate.html;

      const prevDataSnap = await firebase
        .firestore()
        .collection("emailTemplates")
        .doc(emailTemplate.id)

      const prevData = (await prevDataSnap.get()).data()

      await prevDataSnap
        .update({
          html: htmlTemplate || "",
          subject: emailTemplate.subject || "",
          title: emailTemplate.title || "",
          typeId: emailTemplate.typeId || "",
          type: emailTemplate.type || "",
          isEnabled: emailTemplate.isEnabled || false,
          lastUpdatedDateTime: firebase.firestore.Timestamp.fromDate(new Date()),
          templateDataProperties: emailTemplate.templateDataProperties || []
        })
        .then(async () => {

          //if email template status chnaged to 'true' then set any template in the current type to 'false'
          if (!prevData.isEnabled && emailTemplate.isEnabled === true
            && emailTemplate.type != DefaultEmailTemplateType.ServiceRequestToVendor //exceptions
          ) {
            const type = emailTemplate.type
            const typeId = emailTemplate.typeId
            const facilityId = emailTemplate.facilityId
            let query = null

            if (type) {
              query = firebase.firestore().collection('emailTemplates').where('facilityId', '==', facilityId).where('type', '==', type).where('isEnabled', '==', true)
            }
            else if (typeId) {
              query = firebase.firestore().collection('emailTemplates').where('facilityId', '==', facilityId).where('typeId', '==', typeId).where('isEnabled', '==', true)
            }
            else {
              const error = `emailTemplate ${emailTemplate.id} does not contains type`
              console.error(error)
            }

            if (query) {
              await query.get()
                .then(async (snap: any) => {
                  const docs = snap.docs.filter((x: any) => x.id != emailTemplate.id)
                  console.log(`detected ${docs.length} enabled items in emailTemplate group. (type: ${type}).`)
                  if (docs.length > 0) {
                    await docs.forEach(async (doc: any) => {
                      await doc.ref.update({
                        isEnabled: false
                      });
                    });
                  }
                });
            }
          }
          dispatch("getEmailTemplate", emailTemplate.id);
        });
    },

    async updateEmailTemplateByData({ dispatch, commit }, updateData) {
      const id = updateData.id
      updateData.lastUpdatedDateTime = firebase.firestore.Timestamp.fromDate(new Date())
      delete updateData.id
      await firebase
        .firestore()
        .collection("emailTemplates")
        .doc(id)
        .update(updateData)
        .then(Snap => {
          dispatch("getEmailTemplate", id);
        });
    },

    async createEmailTemplate({ dispatch, commit }, emailTemplate) {
      emailTemplate.facilityId = this.state.authModule.currentFacility;
      emailTemplate.createdDateTime = firebase.firestore.Timestamp.fromDate(new Date());
      emailTemplate.isEnabled = true

      await firebase
        .firestore()
        .collection("emailTemplates")
        .add(emailTemplate)
        .then(Snap => {
          router.push("/settings/emailTemplate/" + Snap.id);
        });
    },

    async createEmailTemplateType({ dispatch, commit }, emailTemplateType) {
      delete emailTemplateType.id
      emailTemplateType.facilityId = this.state.authModule.currentFacility;
      emailTemplateType.createdDateTime = firebase.firestore.Timestamp.fromDate(new Date());

      return await firebase
        .firestore()
        .collection("emailTemplateTypes")
        .add(emailTemplateType)
        .then(async snap => {
          await dispatch("getFacilityEmailTemplateTypes");
          return snap
        });
    },

    async getEmailTemplateTypesByMailingList({ dispatch, commit }, mailingList) {

      const emailTemplteTypesSnap = (await firebase.firestore()
        .collection("emailTemplateTypes")
        .where('facilityId', '==', mailingList.facilityId)
        .where('mailingListIds', 'array-contains', mailingList.id).get())

      const emailTemplteTypeIds = emailTemplteTypesSnap.docs.map(x => x.id)

      return await getWhereIdInIds('typeId', emailTemplteTypeIds, 'emailTemplates', mailingList.facilityId)
    },

    async updateEmailTemplateType({ dispatch, commit }, emailTemplateType) {
      return await firebase
        .firestore()
        .collection("emailTemplateTypes")
        .doc(emailTemplateType.id)
        .update({
          name: emailTemplateType.name,
          mailingListIds: emailTemplateType.mailingListIds,
          rulesIds: emailTemplateType.rulesIds,
          lastUpdatedDateTime: firebase.firestore.Timestamp.fromDate(new Date())
        })
        .then(async snap => {
          await dispatch("getFacilityEmailTemplateTypes");

          return snap
        });
    },

    async deleteEmailTemplateType({ dispatch }, id) {
      const templateType = await firebase.firestore().collection("emailTemplateTypes").doc(id).get()
      await firebase
        .firestore()
        .collection("emailTemplateTypes")
        .doc(id)
        .delete().then(async () => {
          const rulesIds = templateType.data().rulesIds
          if (rulesIds && rulesIds.length) {
            const businessRules = await firebase.firestore().collection('businessRules')
              .where('facilityId', '==', this.state.authModule.currentFacility)
              .where(firebase.firestore.FieldPath.documentId(), 'in', rulesIds).get()
            businessRules.docs.forEach(doc => doc.ref.delete())
          }
        });
      dispatch("getFacilityEmailTemplateTypes");
    },

    async deleteEmailTemplate({ dispatch }, id) {
      await firebase
        .firestore()
        .collection("emailTemplates")
        .doc(id)
        .delete();
      dispatch("getFacilityEmailTemplates");
    },

    async sendBroadcast({ dispatch, commit }, broadcast) {
      const doc = {
        facilityId: this.state.authModule.currentFacility,
        sentDatetime: firebase.firestore.Timestamp.now(),
        newBroadcastTemplate: broadcast.template,
        newBroadcastbyEmail: broadcast.email,
        newBroadcastbyPush: broadcast.push,
        newBroadcastbySMS: broadcast.sms,
        status: broadcast.status,
        html: broadcast.html
      };

      await firebase.firestore().collection("broadcasts").add(doc);
    },

    async sendTestEmail(_, payload) {
      payload.facilityId = this.state.authModule.currentFacility;
      await firebase
        .firestore()
        .collection("email")
        .add(payload);
    },

    async getFacility({ dispatch, commit }) {

      if(!this.state.authModule.currentFacility) return
      
      try {
        let currentfacility = serializer.documentSnapshotToDocument<any>(await firebase.firestore().collection("facility").doc(this.state.authModule.currentFacility).get())

        console.log("getting facility: ", currentfacility?.id);

        if (currentfacility != null) {
          currentfacility = initFacility(currentfacility)

          commit("setSelectedFacility", currentfacility);
          commit("setCurrentFacilityData", currentfacility);
          dispatch("subscribeOnCurrentFacility", currentfacility.id);

          return currentfacility;
        }
      } catch (error) {
          console.error("Error getting facility: ", error);
          return null
      }
    },

    async updateFacility({ dispatch, commit }, facility) {
      facility.facilityId = facility.id;
      return await firebase
        .firestore()
        .collection("facility")
        .doc(facility.id)
        .update(facility)
        .then(Snap => {
          dispatch("getFacility", { id: facility.id });

          if (!facility.isOptInLifestylePrograms) {
            dispatch("cleanLifeStyleProgramsMembers");
          }
        });
    },

    async getOrgUsers({ commit }, id) {

      const users = firebase
        .firestore()
        .collection("users")
        .where("orgId", "==", id)
        .where("accessLevel", "in", Object.values(StaffAccessLevels));

      await users.get().then(querySnapshot => {
        const usersList: Array<any> = [];

        querySnapshot.docs.forEach(x => {
          const thisUsers = x.data();
          thisUsers.id = x.id;
          usersList.push(thisUsers);
        });
        commit("setSelectedFacilityUsers", usersList);
      });
    },

    async getCurrentFacilityUsers({ commit }) {

      const users = firebase
        .firestore()
        .collection("users")
        .where("facilityId", "==", this.state.authModule.currentFacility)
        .where("accessLevel", "in", Object.values(StaffAccessLevels));

      await users.get().then(querySnapshot => {
        const usersList: Array<any> = [];

        querySnapshot.docs.forEach(x => {
          const thisUsers = x.data();
          thisUsers.id = x.id;
          usersList.push(thisUsers);
        });
        commit("setCurrentFacilityUsers", usersList);
      });
    },

    async createNewFacilityUser({ dispatch }, { user, orgId }) {
      user.createdDateTime = firebase.firestore.Timestamp.fromDate(new Date())
      user.options = {
        webNotifications: {
          serviceRequestCreated: false,
          serviceRequestCompleted: false,
          unattendedEventEnded: false,
          maintenanceRequestCreated: false,
          deviceAssigned: false,
          deviceUnassigned: false,
          visitToResidentCreated: false,
          bookingsAndOrdersComment: false,
          messagesFromFamily: true,
        },
        emailNotifications: {
          serviceRequestCreated: false,
          serviceRequestCompleted: false,
          unattendedEventEnded: false,
          maintenanceRequestCreated: false,
          deviceAssigned: false,
          deviceUnassigned: false,
          visitToResidentCreated: false,
          bookingsAndOrdersComment: false,
          lifestyleEventsReport: false,
          messagesFromFamily: true,
        }
      }
      return await firebase
        .firestore()
        .collection("users")
        .add(user)
        .then(async a => {
          return await dispatch("getOrgUsers", orgId);
        });
    },

    async updateUserAccessLevel({ dispatch }, { id, accessLevel, facilityId, isEnabledImpersonation, maintenanceType, maintenanceCategories }) {
      isEnabledImpersonation = isEnabledImpersonation ? isEnabledImpersonation : false;

      const inputObject = {
        accessLevel,
        facilityId,
        isEnabledImpersonation,
        maintenanceType,
        maintenanceCategories
      };

      const filteredObject = Object.fromEntries(
        Object.entries(inputObject).filter(([key, value]) => value != null && value !== "")
      );

      await firebase
        .firestore()
        .collection("users")
        .doc(id)
        .update(filteredObject);

      await dispatch("getSelectedUser", id);
      await dispatch("getFacilitiesForOrg");
      await dispatch("getUser");
    },

    async updateUserAccessLevelAndRole({ dispatch }, { id, accessLevel, facilityId, roles, rolesIds, isEnabledImpersonation, maintenanceType, maintenanceCategories }) {
      isEnabledImpersonation = isEnabledImpersonation || false

      const inputObject = {
        accessLevel,
        facilityId,
        roles,
        rolesIds,
        isEnabledImpersonation,
        maintenanceType,
        maintenanceCategories,
      };

      const filteredObject = Object.fromEntries(
        Object.entries(inputObject).filter(([key, value]) => value != null && value !== "")
      );

      await firebase
        .firestore()
        .collection("users")
        .doc(id)
        .update(filteredObject);

      await dispatch("getSelectedUser", id);
      await dispatch("getFacilitiesForOrg");
      await dispatch("getUser");
    },

    async updateUserPhoto({ dispatch }, { id, profilePicUrl, thumbnailUrl }) {
      await firebase
        .firestore()
        .collection("users")
        .doc(id)
        .update({ profilePicUrl, thumbnailUrl });

      await dispatch("getSelectedUser", id);
      await dispatch("getFacilitiesForOrg");
      await dispatch("getUser");
    },

    async subscribeOnCurrentFacility({ commit, state }, facilityId: string) {
    
      // Check if an existing unsubscribe function is stored in state
      if (state.onCurrentFacilityUnsubscribe !== null) {
        return;
      }

      const query = firebase.firestore().collection("facility").doc(facilityId);
      console.log("Subscribed to facility: ", facilityId);
    
      // Create and store the unsubscribe function
      state.onCurrentFacilityUnsubscribe = await query.onSnapshot((snapshot) => {
        const document = snapshot.data();
        if (document) {
          document.id = snapshot.id;
          const facility = initFacility(document);
          commit("setCurrentFacilityData", facility);
          commit("setSelectedFacility", facility);
          console.log(`facility ${document?.id} updated`);
        }
      });
    },

    async unsubscribeOnCurrentFacility({state}) {
      if (state.onCurrentFacilityUnsubscribe) {
          state.onCurrentFacilityUnsubscribe();
          state.onCurrentFacilityUnsubscribe = null;
      }
    },

    async getCareAPIKey({commit, state}, facilityId: string) {
      await firebase
        .firestore()
        .collection("facility")
        .doc(facilityId)
        .collection("sensitive-data")
        .where('type', '==', 'careApi')
        .get()
        .then((querySnapshot) => {
          const key: any = serializer.querySnapshotToDocuments(querySnapshot);
          const isFacilityHasCareAPI = key[0]?.apiKey.length ? true : false;
          commit("setCareAPIKey", isFacilityHasCareAPI);
        })
    },

    async getCareAPIParameters({ state, commit }) {
      await firebase
        .firestore()
        .collection("facility")
        .doc(state.selectedFacility.id)
        .collection("parameters")
        .where('type', '==', 'careApi')
        .get()
        .then((querySnapshot) => {
          const doc = serializer.querySnapshotToDocuments(querySnapshot);
          commit("setCareAPIParameters", doc);
        })
    },

    async updateCareAPIParameters({ state }, careSettings) {
      const facilityRef = await firebase
        .firestore()
        .collection("facility")
        .doc(state.selectedFacility.id);

      const subCollectionRef = facilityRef.collection('parameters');

      subCollectionRef.get()
        .then((querySnapshot) => {
          if(querySnapshot.docs.length) {
            const parameterId = querySnapshot.docs
              .filter(x => x.data().type === "careApi")
              .map(x => x.id)[0];

            if(parameterId && parameterId.length) {
              subCollectionRef.doc(parameterId).update({
                parameters: careSettings
              });
            } else {
              subCollectionRef.add({
                type: "careApi",
                parameters: careSettings
              });
            }
          } else {
            subCollectionRef.add({
              type: "careApi",
              parameters: careSettings
            });
          }
        }).catch((err) => {
        console.log("ERROR ", err)
      })
    },

    async getWeatherData({ state, commit }) {
      console.log('getWeatherData: getting location...')

     const location: any = await getLocation()

     if(!location || !location.latitude || !location.longitude){
        console.log('SET DEFAULT/facility location')
        location.latitude = state.selectedFacility?.defaultLocation?.latitude;
        location.longitude = state.selectedFacility?.defaultLocation?.longitude;
     }

     const unit = state.selectedFacility.countryIso === "US"?"F":"C"
     
     console.log(`unit: ${unit} getting weather data for location: ${location}`)

    await firebase.app().functions(getRegionName())
        .httpsCallable("weather-getByLocation")({ latitude: location.latitude, longitude: location.longitude, unit: unit })
        .then(async (result: any) => {
          console.log('_____got weather data. dateTime: ', new Date())

          if(result.data){
            result.data.location = location
          }

          console.log('setCurrentWeatherData: ', result.data)
          commit("setCurrentWeatherData", result.data);
        });
    },
  }
};
export default facilityModule;
